import { useState } from 'react'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'

import CopyOutlinedIcon from '@ant-design/icons/CopyOutlined'
import FileOutlinedIcon from '@ant-design/icons/FileOutlined'
import FileSearchOutlinedIcon from '@ant-design/icons/FileSearchOutlined'
import SaveOutlinedIcon from '@ant-design/icons/SaveOutlined'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import InputAdornment from '@mui/material/InputAdornment'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Link from '@components/_mui/Link'
import Typography from '@components/_mui/Typography'

import Image from '@shared/components/src/Image'
import { useMe } from '@shared/providers/src/MeProvider'
import { handleError, isImage, isPdf, LabTypes, UserRole } from '@shared/utils'

import CopyToClipboard from '@components/CopyToClipboard'
import Property from '@components/Details/Property'
import Section from '@components/Details/Section'
import FileHover from '@components/FileHover'
import { PdfStatic } from '@components/PdfViewer'

import { usePatientLabFileRename } from '../Lab.hooks'

export default function ResultsSection({ data, mini = false }) {
  const me = useMe()

  const hasObservations = data.observations?.length > 0
  const hasResultFiles = data.resultFiles?.length > 0
  const hasHivSelfTestResult = Boolean(data.hivSelfTest)
  const noResultsAvailable = !hasObservations && !hasResultFiles && !hasHivSelfTestResult

  const isManual = data.lab === LabTypes.Manual
  const isQuest = data.lab === LabTypes.Quest
  const title = isManual ? 'Uploaded Lab Work' : 'Results'

  return (
    <Section
      mini={mini}
      title={title}
      icon={mini ? null : <FileSearchOutlinedIcon />}
      action={
        hasObservations ? (
          <CopyToClipboard>
            {({ copy }) => (
              <Button endIcon={<CopyOutlinedIcon />} size="small" onClick={() => copy(gatherObservations(data.observations))}>
                Copy
              </Button>
            )}
          </CopyToClipboard>
        ) : null
      }
    >
      {noResultsAvailable && <Typography>No results available</Typography>}
      {hasResultFiles &&
        data.resultFiles.map((file) => (
          <ResultFile
            key={file.uid}
            patientId={data.user?.id}
            labId={data.id}
            file={file}
            allowRename={isManual && me.role === UserRole.Provider}
          />
        ))}
      {hasObservations && <ObservationsTable isQuest={isQuest} observations={data.observations} />}
      {hasHivSelfTestResult && <HivSelfTest data={data.hivSelfTest} />}
    </Section>
  )
}

function ObservationsTable({ isQuest, observations }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Test Name</TableCell>
            <TableCell>Result</TableCell>
            {isQuest && <TableCell>Expected Range</TableCell>}
            {isQuest && <TableCell>Status</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {observations.map((observation, index) => (
            <ObservationRow key={index} isQuest={isQuest} observation={observation} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function HivSelfTest({ data }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Lot #</TableCell>
            <TableCell>Collection Date</TableCell>
            <TableCell>Expiration Date</TableCell>
            <TableCell>Result</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>{data.lotNumber}</TableCell>
            <TableCell>{dayjs(data.collectionDate).format('L')}</TableCell>
            <TableCell>{dayjs(data.expirationDate).format('L')}</TableCell>
            <TableCell>{data.testResult}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const getStatusColor = (status) => {
  if (!status) return 'default'
  switch (status) {
    case 'H':
    case 'HH':
      return 'error'
    case 'L':
    case 'LL':
      return 'warning'
    case 'A':
      return 'warning'
    case 'AA':
      return 'error'
    case 'N':
      return 'success'
    default:
      return 'default'
  }
}

const getStatusLabel = (status) => {
  if (!status) return null
  switch (status) {
    case 'H':
      return 'High'
    case 'HH':
      return 'Very High'
    case 'L':
      return 'Low'
    case 'LL':
      return 'Very Low'
    case 'A':
      return 'Alpha Abnormal'
    case 'AA':
      return 'Alpha Very Abnormal'
    case 'N':
      return 'Normal'
    default:
      return status
  }
}

function ObservationRow({ isQuest, observation }) {
  const { testName, name, result, units, expectedRange, status } = observation

  const displayName = testName || name || 'Unnamed Test'
  const statusColor = getStatusColor(status)
  const statusLabel = getStatusLabel(status)

  return (
    <TableRow hover>
      <TableCell>
        <Typography>{displayName}</Typography>
      </TableCell>
      <TableCell>
        <Typography>
          {result}
          {isQuest && units && (
            <Typography component="span" sx={{ ml: 0.5, color: 'text.secondary' }}>
              {units}
            </Typography>
          )}
        </Typography>
      </TableCell>
      {isQuest && (
        <TableCell>
          <Typography sx={{ color: 'text.secondary' }}>{expectedRange || '—'}</Typography>
        </TableCell>
      )}
      {isQuest && (
        <TableCell>
          {status ? (
            <Chip label={statusLabel} color={statusColor} size="small" variant="outlined" />
          ) : (
            <Typography sx={{ color: 'text.secondary' }}>—</Typography>
          )}
        </TableCell>
      )}
    </TableRow>
  )
}

ResultsSection.Loading = ({ title = 'Results', mini = false }) => (
  <Section mini={mini} title={title} icon={mini ? null : <FileSearchOutlinedIcon />}>
    <Paper variant="outlined">
      <Property.Loading label={<Skeleton />} />
      <Property.Loading label={<Skeleton />} />
      <Property.Loading label={<Skeleton />} />
    </Paper>
    <Skeleton variant="rounded" height={400} />
  </Section>
)

function ResultFile({ allowRename, patientId, labId, file }) {
  const [filename, setFilename] = useState(file.filename)

  const rename = usePatientLabFileRename(patientId, labId, file.id)

  const handleSave = () => {
    if (!filename) return
    if (file.filename === filename) return

    return rename
      .mutateAsync({ filename })
      .then(() => toast.success('File renamed successfully'))
      .catch((e) => {
        setFilename(file.filename)
        return handleError(e)
      })
  }

  const handleBlur = () => {
    if (!filename) setFilename(file.filename)
  }

  const image = isImage(file.mimetype)
  const pdf = isPdf(file.mimetype)

  if (image || pdf) {
    return (
      <Stack>
        <TextField
          hiddenLabel
          variant="standard"
          value={filename}
          disabled={rename.isPending}
          onChange={(e) => setFilename(e.target.value)}
          onBlur={handleBlur}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start">Name:</InputAdornment>,
              endAdornment: allowRename ? (
                <InputAdornment position="end">
                  <Button
                    onClick={handleSave}
                    loading={rename.isPending}
                    disabled={!filename || file.filename === filename}
                    endIcon={<SaveOutlinedIcon />}
                  >
                    Save
                  </Button>
                </InputAdornment>
              ) : undefined,
            },
            htmlInput: { readOnly: !allowRename },
          }}
        />
        {image && (
          <Link target="_blank" href={file.url} sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
            <Image
              src={file.url}
              fit="contain"
              duration={500}
              errorIcon={<FileOutlinedIcon />}
              style={{ width: 'unset', height: 'unset', maxWidth: '100%', maxHeight: '100%', minHeight: 200 }}
            />
            <FileHover />
          </Link>
        )}
        {pdf && <PdfStatic url={file.url} filename={file.filename} initialScale={1.2} />}
      </Stack>
    )
  }

  return null
}

const observationToString = (ob) => `${ob.testName || ob.name}: ${ob.result}`
const gatherObservations = (observations = []) => observations.map((ob) => observationToString(ob)).join('\n')
